import { useState } from 'react';
import { Cpu, Tag } from 'lucide-react';
import type { CapabilityMethod } from '@/types';
import {
    MethodSchemaDrawer,
    type MethodSchemaDrawerPayload,
} from '@/components/capability-detail/MethodSchemaDrawer';

const GRID_STYLE = {
    gridTemplateColumns: 'repeat(auto-fill, minmax(265px, 322px))',
    gap: 'clamp(10px, 2vw, 20px)',
} as const;

const METHOD_CARD_BASE =
    'relative z-0 flex min-h-[140px] w-full flex-col rounded-lg border border-slate-200 border-l-4 bg-white p-3 shadow-sm transition-shadow duration-150 hover:border-slate-300 hover:bg-slate-50';

interface MethodsTabProps {
    methods?: CapabilityMethod[];
}

export function MethodsTab({ methods }: MethodsTabProps) {
    const [schemaDrawer, setSchemaDrawer] = useState<MethodSchemaDrawerPayload | null>(null);

    if (!methods || methods.length === 0) {
        return (
            <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50">
                <div className="flex flex-col items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100">
                        <Cpu size={24} className="text-slate-400" />
                    </div>
                    <p className="text-[15.4px] text-slate-400">No methods defined</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="grid justify-start" style={GRID_STYLE}>
                {methods.map((method) => {
                    const hasInputSchema =
                        method.input_schema && Object.keys(method.input_schema).length > 0;
                    const hasOutputSchema =
                        method.output_schema && Object.keys(method.output_schema).length > 0;
                    const hasSchemaFooter = hasInputSchema || hasOutputSchema;

                    return (
                        <div key={method.id} className={`${METHOD_CARD_BASE} border-l-violet-500`}>
                            <div className="flex min-h-0 w-full flex-1 flex-col">
                                <div className="flex min-h-0 flex-1 flex-col">
                                    <div className="flex flex-row items-start gap-2">
                                        <div className="flex shrink-0 items-center justify-center rounded-md bg-violet-100 p-1.5 text-violet-600">
                                            <Cpu size={20} />
                                        </div>
                                        <div className="flex min-w-0 flex-1 flex-col">
                                            <div className="flex flex-wrap items-center gap-1.5">
                                                <span className="text-left font-mono text-[15.4px] font-bold leading-tight text-slate-800">
                                                    {method.name}
                                                </span>
                                                {method.channels?.map((ch) => (
                                                    <span
                                                        key={ch}
                                                        className="rounded-full bg-sky-50 px-1.5 py-0.5 text-[9.9px] font-medium text-sky-600"
                                                    >
                                                        {ch}
                                                    </span>
                                                ))}
                                                {method.semantics && (
                                                    <span className="rounded-full bg-purple-50 px-1.5 py-0.5 text-[9.9px] font-medium text-purple-600">
                                                        {method.semantics}
                                                    </span>
                                                )}
                                            </div>
                                            {method.description && (
                                                <p className="mt-2 line-clamp-2 text-left text-[11px] leading-tight text-slate-600">
                                                    {method.description}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {method.tags && method.tags.length > 0 && (
                                    <div className="flex flex-wrap items-center gap-1 border-t border-slate-100 pt-1.5 text-[11px] text-slate-500">
                                        <span className="flex flex-wrap items-center gap-1">
                                            <Tag size={11} className="shrink-0 text-slate-400" />
                                            {method.tags.map((tag) => (
                                                <span
                                                    key={tag}
                                                    className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600"
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </span>
                                    </div>
                                )}
                                {hasSchemaFooter && (
                                    <div className="grid grid-cols-2 gap-2 border-t border-slate-100 pt-1.5">
                                        <div className="min-w-0">
                                            {hasInputSchema && (
                                                <details>
                                                    <summary
                                                        className="cursor-pointer text-[11px] text-slate-400 transition-colors hover:text-slate-600"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setSchemaDrawer({
                                                                methodName: method.name,
                                                                schema: method.input_schema,
                                                                kind: 'input',
                                                            });
                                                        }}
                                                    >
                                                        Input Schema
                                                    </summary>
                                                </details>
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            {hasOutputSchema && (
                                                <details>
                                                    <summary
                                                        className="cursor-pointer text-[11px] text-slate-400 transition-colors hover:text-slate-600"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setSchemaDrawer({
                                                                methodName: method.name,
                                                                schema: method.output_schema,
                                                                kind: 'output',
                                                            });
                                                        }}
                                                    >
                                                        Output Schema
                                                    </summary>
                                                </details>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
            <MethodSchemaDrawer
                open={schemaDrawer !== null}
                onOpenChange={(open) => {
                    if (!open) setSchemaDrawer(null);
                }}
                payload={schemaDrawer}
            />
        </>
    );
}
